import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container, Divider, Icon, List, Popup } from 'semantic-ui-react';
import { isArray, uniqBy } from 'lodash';
import { AppConfig } from '../../../Config';
import {
  BodyText,
  ControlContainer,
  ControlContainerRow,
  PageSection,
  Subtitle,
  Title,
} from 'common/styles/page';
import CopilotPromptButtons, { AIPromptButtons } from './CopilotPromptButtons';
import styled from 'styled-components';
import { CopilotIcon, AvatarIcon } from 'common/components/icons';
import { StyledAddButton, StyledIconButton } from 'common/components/button/StyledButton';
import CopilotThreadList from './CopilotThreadList';
import CopilotDisclaimerLink from './CopilotDisclaimerLink';
import ExportThreadButton from './ExportThreadButton';
import ApiData from '../../../common/components/ApiData';
import AwaitingQuery from './AwaitingQuery';
import { MarkdownRenderer } from './MarkdownRenderer';
import { CopilotFilePreviewModal } from './CopilotFilePreviewModal';
import { FileViewModal } from '../../settings/repository-management/components/FileViewModal';
import { copilotApi } from '../api/chatApi';
import { useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from '@aws-amplify/auth';
import { getCurrentOrganization } from 'common/selectors/auth';
import { useSelector } from 'react-redux';

interface Message {
  id: number;
  role: 0 | 1 | 2;
  message: string;
  error?: boolean;
}

interface CopilotMessageProps {
  message: Message;
  onFileLinkClick?: (payload: { href: string; fileId?: string; fileUrl?: string; label: string }) => void;
}


function CopilotMessage(props: CopilotMessageProps) {
  const { message, onFileLinkClick } = props;
  const [textCopied, setTextCopied] = useState(false);

  if (message.error) {
    return (
      <PageSection>
        <BodyText>Error getting response</BodyText>
      </PageSection>
    );
  }

  return (
    <Container
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: message.role === 1 ? 'flex-end' : 'flex-start',
        margin: '5px 0',
      }}
    >
      <QuestionContainer
        style={{
          flexDirection: message.role === 1 ? 'row-reverse' : 'row', // Align avatar and message dynamically
          justifyContent: message.role === 1 ? 'flex-end' : 'flex-start',
        }}
      >
        <Circle>{message.role === 1 ? AvatarIcon() : CopilotIcon()}</Circle>

        <MessageContainer>
          {message.role === 0 ? (
            <MarkdownRenderer content={message.message} onFileLinkClick={onFileLinkClick} />
          ) : (
            message.message
          )}
        </MessageContainer>
        {message.role !== 1 && (
          <IconWrapper>
            <Icon
              name={textCopied ? 'check' : 'copy outline'}
              onClick={() => {
                navigator.clipboard.writeText(message.message);
                setTextCopied(true);
              }}
              color="grey"
              style={{ cursor: 'pointer' }}
              title={textCopied ? 'Copied!' : 'Copy to clipboard'}
            />
          </IconWrapper>
        )}
      </QuestionContainer>
    </Container>
  );
}

type Props = {
  selectedThreadKey?: number | undefined;
  initialPrompt?: string;
  initialTitle?: string;
  onThreadChange?: (threadId: number | undefined) => void;
  conversations?: any;
  conversationsLoading?: boolean;
  conversationsError?: boolean;
};

const Chatbot = ({ selectedThreadKey, initialPrompt, initialTitle, onThreadChange, conversations, conversationsLoading, conversationsError }: Props) => {
  const ws = useRef<WebSocket | null>(null);
  const [threadKey, setThreadKey] = useState<number | undefined>(selectedThreadKey);
  const [token, setToken] = useState<string | null>(null);
  const previousRoomKey = useRef(threadKey);

  // Get ID token from current session (same as HTTP requests)
  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      return null;
    }
  }, []);

  // Initialize token on component mount - only run once
  useEffect(() => {
    let mounted = true;
    getAccessToken().then(accessToken => {
      if (mounted) {
        setToken(accessToken);
      }
    });
    return () => {
      mounted = false;
    };
  }, []); // Remove getAccessToken dependency

  const maxRetries = 5;
  const baseDelay = 1000;

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [promptText, setPromptText] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessingPrompt, setProcessingPrompt] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messagesByThread, setMessagesByThread] = useState<Record<string, Message[]>>({});
  const [promptEvaluated, setPromptEvaluated] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    href: string;
    fileId?: string;
    fileUrl?: string;
    label: string;
  } | null>(null);
  const currentOrganization = useSelector(getCurrentOrganization);
  const [isThreadListOpen, setIsThreadListOpen] = useState(false);

  useEffect(() => {
    if (selectedThreadKey !== undefined) {
      setThreadKey(selectedThreadKey);
    }
  }, [selectedThreadKey]);

  const exportThread = async (threadId: number) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (!idToken || !currentOrganization?.id) return;

      const res = await fetch(
        `${AppConfig.api.apiUrl}/api/portal/v1/chat/copilot/${threadId}/export/`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'x-nulogic-organization-id': currentOrganization.id.toString(),
          },
        }
      );
      if (!res.ok) return;

      const disposition = res.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="(.+)"/);
      let filename = filenameMatch?.[1] || 'conversation.txt';
      if (filename.endsWith('.md')) {
        filename = filename.replace(/\.md$/i, '.txt');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Download failed silently
    }
  };

  const [searchParams] = useSearchParams();
  const paramPromptKey = searchParams.get('prompt');
  const buttonPrompt = initialPrompt ?? paramPromptKey;
  const isModal = initialPrompt !== undefined;

  useEffect(() => {
    if (!threadKey) {
      if (paramPromptKey) {
        const stockPrompt = AIPromptButtons.find(button => button.key === paramPromptKey);
        if (stockPrompt?.prompt) {
          startConversation(stockPrompt.prompt);
        }
      } else if (initialPrompt) {
        startConversation(initialPrompt);
      }
    }
  }, [buttonPrompt, threadKey, initialPrompt, paramPromptKey]);

  const [createThread] = copilotApi.useApiPortalV1ChatCopilotCreateMutation();

  async function startNewThread(prompt: string) {
    let threadName = initialTitle || prompt;
    if (threadName.length > 255) {
      threadName = threadName.slice(0, 255);
    }

    const thread = await createThread({
      chat: {
        name: threadName,
      },
    });
    if (!thread.error) {
      const newThreadId = thread.data?.id;
      if (newThreadId && prompt) {
        setMessagesByThread(prev => {
          const currentMessages = prev[newThreadId.toString()] || [];
          return {
            ...prev,
            [newThreadId.toString()]: [
              ...currentMessages,
              { id: -Date.now(), role: 1, message: prompt },
            ],
          };
        });
      }
      setThreadKey(newThreadId);
      onThreadChange?.(newThreadId);
    }
    return thread.data?.id;
  }

  function startConversation(prompt: string) {
    if (ws?.current?.readyState !== WebSocket.OPEN) {
      setIsConnected(false);
    }

    setPromptText(prompt);

    // Wait for threadKey to be updated, and then the useEffect tied to threadKey will handle room change
    startNewThread(prompt).then(newThreadKey => {
      if (newThreadKey) {
        onThreadChange?.(newThreadKey);
      }
    });
  }

  function newConversation() {
    setPromptText('');
    setThreadKey(undefined);
    setIsConnected(false);
    setProcessingPrompt(false);
    setConnectionError(null);
    setPreviewFile(null);
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    onThreadChange?.(undefined);
  }

  function handleKeyDown(e: { key: any }) {
    if (e.key === 'Enter') {
      startConversation(promptText);
    }
  }

  const handleSendMessage = () => {
    if (ws.current?.readyState !== WebSocket.OPEN) {
      setIsConnected(false);
      return;
    }

    if (ws.current && ws.current.readyState === WebSocket.OPEN && promptText.trim()) {
      setProcessingPrompt(true);
      ws.current.send(
        JSON.stringify({
          type: 'message',
          message: promptText,
        })
      );

      // TODO: Fix the send logic and persist to threads
      //setMessages((prev) => [...prev, { role: "user", message: input }]);
      setPromptText('');
    }
  };

  // Close Websocket after 10 minutes of inactivity
  useEffect(() => {
    const idleTimer = setTimeout(
      () => {
        if (ws.current) {
          ws.current.close();
        }
      },
      10 * 60 * 1000
    ); // 10 minutes
    return () => clearTimeout(idleTimer);
  }, []);

  const handleRoomChange = useCallback(() => {
    let socket: WebSocket | null = null;
    let retryCount = 0;
    let retryTimeout: NodeJS.Timeout | null = null;
    let isConnecting = false;

    const mergeIncomingData = (eventData: any, threadKey: string) => {
      const data = JSON.parse(eventData);
      if (data) {
        const toAppend = isArray(data)
          ? data.map((message: any) => ({
            id: message.id,
            role: message.role,
            message: message.message,
            error: message.error,
          }))
          : [data];

        // Check if this is a single message with role 0 (AI response)
        if (!isArray(data) && data.role === 0) {
          setProcessingPrompt(false);
        }

        // Update messages for the specific threadKey
        setMessagesByThread(prev => {
          const currentMessages = prev[threadKey] || [];
          const filteredCurrentMessages = currentMessages.filter(existing =>
            !(
              existing.id < 0 &&
              toAppend.some((incoming: any) =>
                incoming.role === existing.role && incoming.message === existing.message
              )
            )
          );
          return {
            ...prev,
            [threadKey]: uniqBy([...filteredCurrentMessages, ...toAppend], 'id'),
          };
        });
      }
    };

    const connect = (threadKey: string) => {
      if (!token || isConnecting) {
        return;
      }
      isConnecting = true;

      socket = new WebSocket(`${AppConfig.api.wsApiUrl}/ws/chat/${encodeURIComponent(threadKey)}/`);
      ws.current = socket;

      socket.onopen = () => {
        // Send authentication message with token
        if (socket) {
          socket.send(
            JSON.stringify({
              type: 'authentication',
              token: `Bearer ${token}`,
              organization_id: currentOrganization?.id,
            })
          );
        }
        setIsConnected(true);
        setConnectionError(null);
        retryCount = 0;
        isConnecting = false;
      };

      socket.onmessage = event => {
        try {
          const data = JSON.parse(event.data);

          // Handle authentication errors
          if (data.error) {
            setConnectionError(data.error);
            setIsConnected(false);
            return;
          }

          // Handle initial chat data (sent after successful authentication)
          if (Array.isArray(data)) {
            mergeIncomingData(event.data, threadKey);
            return;
          }

          // Handle regular messages
          mergeIncomingData(event.data, threadKey);
        } catch (error) {
          // Error parsing WebSocket message
        }
      };

      socket.onclose = () => {
        setIsConnected(false);
        isConnecting = false;
        if (threadKey) {
          retryConnection(threadKey.toString());
        }
      };

      socket.onerror = error => {
        isConnecting = false;
      };
    };

    const retryConnection = (threadKey: string) => {
      if (retryCount < maxRetries) {
        const delay = Math.min(baseDelay * 2 ** retryCount, 30000);
        retryCount++;
        retryTimeout = setTimeout(() => connect(threadKey), delay) as any;
      } else {
        setConnectionError(
          'Failed to connect after multiple attempts. Please try refreshing the page.'
        );
      }
    };

    if (
      ws.current &&
      ws.current.readyState === WebSocket.OPEN &&
      ws.current.url.includes(threadKey?.toString() || 'noId')
    ) {
      return;
    }

    if (threadKey) {
      connect(threadKey.toString());
    }

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      if (socket) {
        socket.close();
      }
    };
  }, [threadKey]);

  // Thread change event, call handleRoomChange
  useEffect(() => {
    if (previousRoomKey.current === threadKey) {
      return;
    }

    previousRoomKey.current = threadKey;
    handleRoomChange();
    scrollToTop();
  }, [handleRoomChange, threadKey]);

  // If there's a thread, prompt, and connection, send it
  useEffect(() => {
    if (threadKey && isConnected && promptText.trim()) {
      handleSendMessage();
    }
  }, [threadKey, isConnected]);

  // Reset text window to show progress indicator
  useEffect(() => {
    if (isProcessingPrompt) {
      scrollToBottom();
    }
  }, [isProcessingPrompt]);

  // Scroll to bottom of thread button
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      setShowScrollButton(false);
    } else {
      setShowScrollButton(true);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
    setShowScrollButton(false);
  };

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const isSubmitDisabled = promptText.trim().length === 0 || !isConnected;

  if (!threadKey) {
    return (
      <PageSection style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <Title>Culture Copilot</Title>
          <StyledIconButton
            icon="list"
            title="View conversation history"
            onClick={() => setIsThreadListOpen(!isThreadListOpen)}
          />
        </div>
        <Divider />

        {isThreadListOpen && conversations && (
          <ThreadListPanel>
            <ThreadListHeader>
              <div style={{ fontWeight: 600, fontSize: '16px' }}>Conversation History</div>
              <Icon
                name="close"
                style={{ cursor: 'pointer' }}
                onClick={() => setIsThreadListOpen(false)}
              />
            </ThreadListHeader>
            <ThreadListContent>
              <List>
                <ApiData data={conversations} isLoading={!!conversationsLoading} isError={!!conversationsError}>
                  <CopilotThreadList
                    selectedThreadId={selectedThreadKey!}
                    chatHistory={conversations}
                    setSelectedThreadId={(threadId) => {
                      onThreadChange?.(threadId);
                      setIsThreadListOpen(false);
                    }}
                    onThreadDeleted={() => {
                      onThreadChange?.(undefined);
                      setIsThreadListOpen(false);
                    }}
                  />
                </ApiData>
              </List>
            </ThreadListContent>
          </ThreadListPanel>
        )}

        <PageSection padtop="10px" padleft="15px" padright="15px">
          <Subtitle>Start a conversation!</Subtitle>
          <BodyText>
            If you have questions about your assessment data, culture, and engagement, just ask!
            nuLogic Copilot is here to provide analysis and insights.
          </BodyText>
          <PageSection padtop="20px">
            <ChatInput>
              <Input
                onChange={e => setPromptText(e.target.value)}
                style={{ width: '100%', marginRight: 10 }}
                placeholder="Ask a question about your data."
                onKeyDown={handleKeyDown}
              />
              <SendButton onClick={() => handleKeyDown({ key: 'Enter' })}>
                <Icon name={'send'} />
              </SendButton>
            </ChatInput>
            <DisclaimerRow>
              <CopilotDisclaimerLink>AI responses can be inaccurate, click here to learn more...</CopilotDisclaimerLink>
            </DisclaimerRow>
          </PageSection>
        </PageSection>
        <CopilotPromptButtons startConversation={startConversation} />
      </PageSection>
    );
  } else if (threadKey && !isConnected) {
    if (!token) {
      return (
        <PageSection style={{ fontFamily: 'Poppins, sans-serif' }}>
          <Title>Authentication Required</Title>
          <BodyText>
            You need to be logged in to use the chat feature. Please refresh the page or log in
            again.
          </BodyText>
          <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        </PageSection>
      );
    }

    if (connectionError) {
      return (
        <PageSection>
          <Title>Connection Error</Title>
          <BodyText>{connectionError}</BodyText>
          <Button
            onClick={() => {
              setConnectionError(null);
              handleRoomChange();
            }}
          >
            Retry Connection
          </Button>
        </PageSection>
      );
    }

    return (
      <PageSection>
        <ConnectingContainer>
          <ConnectingSpinner>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </ConnectingSpinner>
          <ConnectingText>Connecting to the thread...</ConnectingText>
          <ConnectingSubtext>Establishing secure connection</ConnectingSubtext>
        </ConnectingContainer>
      </PageSection>
    );
  } else {
    return (
      <Container
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          height: '100%',
          maxHeight: 'calc(100vh - 50px)',
          width: '100%',
          margin: '0 auto',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {!isModal && (
          <>
            <ControlContainerRow>
              <ControlContainer justify="left">
                <Title>Culture Copilot</Title>
              </ControlContainer>
              <ControlContainer>
                <StyledAddButton
                  label="New Conversation"
                  width="180px"
                  height="34px"
                  onClick={() => {
                    newConversation();
                  }}
                />
                <div style={{ marginLeft: '10px', display: 'flex', gap: '4px' }}>
                  <ExportThreadButton
                    threadId={threadKey}
                    onExport={() => threadKey && exportThread(threadKey)}
                  />
                  <StyledIconButton
                    icon="list"
                    title="View conversation history"
                    onClick={() => setIsThreadListOpen(!isThreadListOpen)}
                  />
                </div>
              </ControlContainer>
            </ControlContainerRow>
            <Divider />

            {isThreadListOpen && conversations && (
              <ThreadListPanel>
                <ThreadListHeader>
                  <div style={{ fontWeight: 600, fontSize: '16px' }}>Conversation History</div>
                  <Icon
                    name="close"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setIsThreadListOpen(false)}
                  />
                </ThreadListHeader>
                <ThreadListContent>
                  <List>
                    <ApiData data={conversations} isLoading={!!conversationsLoading} isError={!!conversationsError}>
                      <CopilotThreadList
                        selectedThreadId={selectedThreadKey!}
                        chatHistory={conversations}
                        setSelectedThreadId={(threadId) => {
                          onThreadChange?.(threadId);
                          setIsThreadListOpen(false);
                        }}
                        onThreadDeleted={() => {
                          onThreadChange?.(undefined);
                          setIsThreadListOpen(false);
                        }}
                      />
                    </ApiData>
                  </List>
                </ThreadListContent>
              </ThreadListPanel>
            )}
          </>
        )}

        <Container
          style={{
            height: !isModal ? '100vh' : '70vh',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            overflow: 'hidden',
          }}
        >
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              height: 'calc(100% - 60px)',
              overflowY: 'auto',
            }}
          >
            {messagesByThread[threadKey]?.map(
              (message: Message, index: React.Key | null | undefined) => (
                <CopilotMessage key={index} message={message} onFileLinkClick={setPreviewFile} />
              )
            )}

            {isProcessingPrompt && (
              <AwaitingQuery context_length={100} maxContentWidth="360px" />
            )}

            <div style={{ float: 'left', clear: 'both', height: 50 }} ref={messagesEndRef}></div>
          </div>

          <Container style={{ display: 'flex', gap: '10px' }}>
            {showScrollButton && (
              <Button
                circular
                icon
                color="blue"
                onClick={scrollToBottom}
                style={{
                  position: 'fixed',
                  bottom: '110px', // 100px from the bottom
                  left: 'calc(150px + 50%)', // Center in the right column
                  transform: 'translateX(-50%)', // Adjust for true centering
                  zIndex: 1000, // Ensure it appears above other elements
                }}
              >
                <Icon name="arrow down" />
              </Button>
            )}

            {!isModal && (
              <>
                <input
                  type="text"
                  value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSendMessage();
                    }
                  }}
                  placeholder="Ask a quesion about your data."
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: isSubmitDisabled ? '1px solid rgb(204, 204, 204)' : 'none',
                    backgroundColor: isSubmitDisabled ? '#EFEFEF4D' : '#007bff',
                    color: isSubmitDisabled ? 'black' : 'white',
                    cursor: isSubmitDisabled ? 'unset' : 'pointer',
                  }}
                  disabled={isSubmitDisabled}
                >
                  Send
                </button>
              </>
            )}
          </Container>

          {previewFile?.fileId ? (
            <FileViewModal
              fileId={previewFile.fileId}
              isOpen={Boolean(previewFile)}
              title={previewFile.label}
              setModalOpen={(open: boolean) => {
                if (!open) {
                  setPreviewFile(null);
                }
              }}
            />
          ) : (
            <CopilotFilePreviewModal
              file={previewFile}
              isOpen={Boolean(previewFile)}
              onClose={() => setPreviewFile(null)}
            />
          )}
        </Container>
      </Container>
    );
  }
};

export default Chatbot;

const Input = styled.textarea`
  flex-grow: 1;
  background-color: transparent;
  border: none;
  color: #2a3039;
  font-family: 'Poppins', sans-serif;
  padding: 0 5px; /* Remove padding-top and padding-bottom */
  outline: none;
  resize: none;
  overflow: auto;
  max-height: 300px; /* Height for 4 lines */
  line-height: 24px; /* Adjust the line-height to match the desired initial height */
  width: 100%;

  font-family: Raleway;
  font-size: 14px;

  &::placeholder {
    color: #666d79;
    font-size: 14px;
  }
`;

const DisclaimerRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 8px 0;
  width: 100%;
`;

const SendButton = styled.button`
  background-color: transparent;
  border: none;
  color: #e0e0e0;
  cursor: pointer;
  outline: none;
  padding: 5px;
  margin-left: 5px;
`;

const ChatInput = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #dfdfdf;
  padding: 16px;
  width: 100%;
`;

const Circle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #2d70e2;
  color: #ffffff;
  margin-right: 10px;
  min-width: 40px;
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: -1;
`;

const IconWrapper = styled.div`
  cursor: pointer;
`;

const QuestionContainer = styled.div`
  font-size: 13px;
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;
  max-width: 70%;
  margin-right: 5px;
`;

const MessageContainer = styled.div`
  background: #f8faff;
  border-radius: 8px;
  padding: 16px;
  margin-right: 10px;
`;

const ConnectingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
`;

const ConnectingSpinner = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 24px;

  .spinner-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 3px solid transparent;
    border-top: 3px solid #2d70e2;
    border-radius: 50%;
    animation: spin 1.2s linear infinite;
  }

  .spinner-ring:nth-child(1) {
    animation-delay: 0s;
  }

  .spinner-ring:nth-child(2) {
    width: 60px;
    height: 60px;
    top: 10px;
    left: 10px;
    border-top-color: #4a90e2;
    animation-delay: 0.2s;
  }

  .spinner-ring:nth-child(3) {
    width: 40px;
    height: 40px;
    top: 20px;
    left: 20px;
    border-top-color: #6ab0e2;
    animation-delay: 0.4s;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ConnectingText = styled.div`
  font-family: Raleway;
  font-size: 20px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 8px;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

const ConnectingSubtext = styled.div`
  font-family: Raleway;
  font-size: 14px;
  color: #666d79;
  opacity: 0.8;
  animation: fadeInOut 3s ease-in-out infinite;

  @keyframes fadeInOut {
    0%,
    100% {
      opacity: 0.8;
    }
    50% {
      opacity: 0.4;
    }
  }
`;

const ThreadListPanel = styled.div`
  position: absolute;
  top: 60px;
  right: 20px;
  width: 350px;
  max-height: calc(100vh - 150px);
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const ThreadListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const ThreadListContent = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 8px;
`;
